import { ReportFormat } from '@cobliteam/api-lib';
import { Button, Dialog, Dropdown, Tooltip, Typography } from '@cobliteam/ui/components';

import Icon from '@cobliteam/ui/icons';
import { forwardRef } from 'react';

interface ReportFormatOption {
  label: string;
  value: ReportFormat;
}

const reportFormatOptions: ReportFormatOption[] = [
  { label: 'Excel', value: ReportFormat.EXCEL },
  { label: 'CSV', value: ReportFormat.CSV },
];

interface ExportButtonProps {
  enableMaximumLinesTooltip: boolean;
  disableButton: boolean;
  availableFormats?: ReportFormat[];
  onOpen: (format: ReportFormat) => void;
}

interface ButtonWithTooltipProps {
  enableMaximumLinesTooltip: boolean;
  disableButton: boolean;
  showEndIcon: boolean;
  availableFormats?: ReportFormat[];
  onOpen: (format: ReportFormat) => void;
}

const ButtonWithTooltip = forwardRef<HTMLButtonElement, ButtonWithTooltipProps>(({ enableMaximumLinesTooltip, disableButton, availableFormats, onOpen }, ref) => (
  <Tooltip asChild align='end' content={enableMaximumLinesTooltip ? 'Refaça os filtros para reduzir a quantidade de linhas' : 'Exportar'} position='bottom' className='max-w-44'>
    <Button
      ref={ref}
      data-testid='export-button'
      variant='outlined'
      size='sm'
      color='primary'
      disabled={disableButton}
      onClick={disableButton ? undefined : availableFormats ? undefined : () => onOpen(ReportFormat.EXCEL)}
      icon={<Icon name='download' />}
    />
  </Tooltip>
));

const ExportButton = ({ enableMaximumLinesTooltip, disableButton, availableFormats, onOpen }: ExportButtonProps) => {
  const showEndIcon = !!(availableFormats && availableFormats.length > 1);

  if (disableButton) {
    return (
      <ButtonWithTooltip
        enableMaximumLinesTooltip={enableMaximumLinesTooltip}
        disableButton={disableButton}
        showEndIcon={showEndIcon}
        availableFormats={availableFormats}
        onOpen={onOpen}
      />
    );
  }

  if (availableFormats) {
    const availableReportFormatOptions = reportFormatOptions.filter((option) => availableFormats.includes(option.value));
    return (
      <Dropdown
        placement='bottom-end'
        overlay={
          <div className='flex w-40 flex-col rounded-md border border-gray-light bg-white'>
            <Dropdown.List
              heightList={58}
              heightItem={30}
              data={availableReportFormatOptions || []}
              className='overflow-visible'
              itemComponent={(item: ReportFormatOption) => (
                <div className='flex h-7 w-full cursor-pointer items-center px-2.5 hover:bg-gray-lightest' onClick={() => onOpen(item.value)}>
                  <Typography variant='description-list-medium' color='secondary'>
                    Exportar para {item.label}
                  </Typography>
                </div>
              )}
            />
          </div>
        }
      >
        <ButtonWithTooltip
          enableMaximumLinesTooltip={enableMaximumLinesTooltip}
          disableButton={disableButton}
          showEndIcon={showEndIcon}
          availableFormats={availableFormats}
          onOpen={onOpen}
        />
      </Dropdown>
    );
  }

  return (
    <Dialog.Trigger asChild>
      <ButtonWithTooltip
        enableMaximumLinesTooltip={enableMaximumLinesTooltip}
        disableButton={disableButton}
        showEndIcon={showEndIcon}
        availableFormats={availableFormats}
        onOpen={onOpen}
      />
    </Dialog.Trigger>
  );
};

export default ExportButton;
